/*
 * SSN wizard input component
 * <p>
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2020 Gallagher Bassett Services, Inc.
 *
 */

import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Renderer2,
  ViewChild
} from '@angular/core';

import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';

@Component ( {
    selector: 'reporter-ssn',
    templateUrl: './ssn.component.html',
    styleUrls: ['./ssn.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SsnComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => SsnComponent),
            multi: true
        },
    ],
    standalone: false
} )

export class SsnComponent implements ControlValueAccessor, Validator, AfterViewInit {

  constructor ( private renderer: Renderer2, private elementRef: ElementRef ) {
  }

  set value ( v: any ) {
    if ( v !== this.innerValue ) {
      this.innerValue = v;
      this.onChange ( v );
    }
  }

  get value () {
    return `${ this.innerValue }`.replace ( /\D/g, '' );
  }

  get required (): boolean {
    return this.elementRef.nativeElement.attributes.hasOwnProperty ( 'required' );
  }

  innerValue: any;
  initialized = false;
  initialValue: any;

  @ViewChild ( 'field', { static: true } ) field: ElementRef;
  onChange = ( _: any ) => {
  };
  onTouched = () => {
  };

  ngAfterViewInit () {
    if ( this.required ) {
      this.renderer.setAttribute ( this.field.nativeElement, 'required', '' );
    }
    if ( this.elementRef.nativeElement.classList.contains ( 'required' ) ) {
      this.renderer.addClass ( this.field.nativeElement, 'required' );
    }
    this.initialized = true;
  }

  writeValue ( value: any ): void {
    if ( this.initialValue === undefined ) {
      this.initialValue = value;
    } else {
      this.initialValue = value || null;
    }

    if ( /^\*{3}-?\*{2}-?\d{4}$/.test ( value ) && value === this.initialValue ) {
      this.innerValue = value;
      return;
    }

    let newVal = `${ value }`.replace ( /\D/g, '' );
    if ( newVal.length === 0 ) {
      newVal = '';
    } else if ( newVal.length <= 3 ) {
      newVal = newVal.replace ( /^(\d{0,3})/, '$1' );
    } else if ( newVal.length <= 5 ) {
      newVal = newVal.replace ( /^(\d{0,3})(\d{0,2})/, '$1-$2' );
    } else if ( newVal.length <= 9 ) {
      newVal = newVal.replace ( /^(\d{0,3})(\d{0,2})(\d{0,3})/, '$1-$2-$3' );
    } else {
      newVal = newVal.substring ( 0, 9 );
      newVal = newVal.replace ( /^(\d{0,3})(\d{0,2})(\d{0,3})/, '$1-$2-$3' );
    }

    this.innerValue = newVal;
    this.renderer.setProperty ( this.elementRef.nativeElement, 'value', newVal.replace ( /\D/g, '' ) || undefined );
    this.renderer.setProperty ( this.field.nativeElement, 'value', newVal );
    this.onChange ( newVal.replace ( /\D/g, '' ) || undefined );
  }

  registerOnChange ( fn: ( value: any ) => any ): void {
    this.onChange = fn;
  }

  registerOnTouched ( fn: () => any ): void {
    this.onTouched = fn;
  }

  setDisabledState ( isDisabled: boolean ): void {
    this.renderer.setProperty ( this.elementRef.nativeElement, 'disabled', isDisabled );
  }

  validate ( control: FormControl ): ValidationErrors {
    if ( !this.initialized ) {
      return null;
    }
    if ( !control.value && this.required ) {
      return {
        required: true,
        message: ' is required.'
      };
    }

    if ( control.value
      && /^\*{3}-?\*{2}-?\d{4}$/.test ( control.value )
      && control.value !== this.initialValue ) {
      return {
        patternMismatch: true,
        message: ' must be a valid SSN.'
      };
    }

    if ( control.value
      && !/^\d{3}-?\d{2}-?\d{4}$/.test ( control.value )
      && !/^\*{3}-?\*{2}-?\d{4}$/.test ( control.value ) ) {
      return {
        patternMismatch: true,
        message: ' must be a valid SSN.'
      };
    }

    return null;
  }
}
